import { Plus, Search, Grid, List, Trash2 } from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/components/ui/use-toast'
import { useProjectPhaseStore } from '@/stores/projectPhaseStore'
import { getAxiosInstance } from '../../network/axiosInstance'
import { BlueprintWithDetails, BLUEPRINT_CATEGORIES } from '../../types/blueprints'
import { BlueprintCard } from './BlueprintCard'
import { BlueprintEditor } from './BlueprintEditor'

export const BlueprintsPage: React.FC = () => {
  const { toast } = useToast()
  const { setPendingBlueprint } = useProjectPhaseStore()
  const [blueprints, setBlueprints] = useState<BlueprintWithDetails[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [activeTab, setActiveTab] = useState('my-blueprints')
  
  // Editor state
  const [showEditor, setShowEditor] = useState(false)
  const [editingBlueprint, setEditingBlueprint] = useState<BlueprintWithDetails | undefined>()
  const [editorMode, setEditorMode] = useState<'create' | 'edit' | 'view'>('create')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [blueprintToDelete, setBlueprintToDelete] = useState<BlueprintWithDetails | null>(null)
  
  // Fetch blueprints
  const fetchBlueprints = async () => {
    try {
      setLoading(true)
      const params: any = {}
      
      if (searchTerm) params.search = searchTerm
      if (selectedCategory !== 'all') params.category = selectedCategory

      const response = await getAxiosInstance().get('/api/blueprints', { params })
      setBlueprints(response.data.blueprints || [])
    } catch (error: any) {
      console.error('Error fetching blueprints:', error)
      
      // Handle different error types gracefully
      if (error.response?.status === 401) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to view blueprints',
          variant: 'destructive'
        })
      } else if (error.response?.status === 403) {
        toast({
          title: 'Access Denied',
          description: 'You do not have permission to view blueprints',
          variant: 'destructive'
        })
      } else {
        toast({
          title: 'Error',
          description: error.message || 'Failed to load blueprints',
          variant: 'destructive'
        })
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBlueprints()
  }, [searchTerm, selectedCategory])
  const handleCreateBlueprint = () => {
    setEditingBlueprint(undefined)
    setEditorMode('create')
    setShowEditor(true)
  }

  const handleEditBlueprint = (blueprint: BlueprintWithDetails) => {
    setEditingBlueprint(blueprint)
    setEditorMode('edit')
    setShowEditor(true)
  }

  const handleRunBlueprint = (_blueprint: BlueprintWithDetails) => {
    // TODO: Open run blueprint dialog
    toast({
      title: 'Feature Coming Soon',
      description: 'Blueprint execution will be available soon'
    })
  }

  const handleSimulateBlueprint = (blueprint: BlueprintWithDetails) => {
    // Open blueprint in simulation mode
    setEditingBlueprint(blueprint)
    setEditorMode('view')
    setShowEditor(true)
    
    toast({
      title: 'Simulation Mode',
      description: `Opening "${blueprint.name}" in simulation mode`
    })
  }

  const handleViewBlueprintDetails = (blueprint: BlueprintWithDetails) => {
    // Open blueprint in view-only mode with detailed information
    setEditingBlueprint(blueprint)
    setEditorMode('view')
    setShowEditor(true)
  }

  const handleBlueprintSave = async (blueprintData: Partial<BlueprintWithDetails>) => {
    try {
      if (editorMode === 'edit' && editingBlueprint?.id) {
        await getAxiosInstance().put(`/api/blueprints/${editingBlueprint.id}`, blueprintData)
      } else {
        await getAxiosInstance().post('/api/blueprints', blueprintData)
      }
      
      toast({
        title: 'Success',
        description: `Blueprint ${editorMode === 'edit' ? 'updated' : 'created'} successfully`
      })
      
      setShowEditor(false)
      fetchBlueprints() // Refresh list
    } catch (error: any) {
      console.error(`Error ${editorMode}ing blueprint:`, error)
      
      // Handle different error types gracefully
      if (error.response?.status === 401) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to save blueprints',
          variant: 'destructive'
        })
      } else if (error.response?.status === 403) {
        toast({
          title: 'Access Denied',
          description: 'You do not have permission to save blueprints',
          variant: 'destructive'
        })
      } else {
        toast({
          title: 'Error',
          description: error.response?.data?.message || error.message || `Failed to ${editorMode} blueprint`,
          variant: 'destructive'
        })
      }
    }
  }

  const handleEditorCancel = () => {
    setShowEditor(false)
    setEditingBlueprint(undefined)
  }
  const handleDuplicateBlueprint = async (blueprint: BlueprintWithDetails) => {
    try {
      await getAxiosInstance().post(`/api/blueprints/${blueprint.id}/duplicate`)
      
      toast({
        title: 'Success',
        description: 'Blueprint duplicated successfully'
      })
      
      fetchBlueprints() // Refresh list
    } catch (error: any) {
      console.error('Error duplicating blueprint:', error)
      
      // Handle different error types gracefully
      if (error.response?.status === 401) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to duplicate blueprints',
          variant: 'destructive'
        })
      } else if (error.response?.status === 403) {
        toast({
          title: 'Access Denied',
          description: 'You do not have permission to duplicate blueprints',
          variant: 'destructive'
        })
      } else {
        toast({
          title: 'Error',
          description: error.response?.data?.message || error.message || 'Failed to duplicate blueprint',
          variant: 'destructive'
        })
      }
    }
  }

  const handleDeleteBlueprint = async (blueprint: BlueprintWithDetails) => {
    setBlueprintToDelete(blueprint)
    setShowDeleteDialog(true)
  }

  const confirmDeleteBlueprint = async () => {
    if (!blueprintToDelete) return

    try {
      await getAxiosInstance().delete(`/api/blueprints/${blueprintToDelete.id}`)
      
      toast({
        title: 'Success',
        description: 'Blueprint deleted successfully'
      })
      
      setShowDeleteDialog(false)
      setBlueprintToDelete(null)
      fetchBlueprints() // Refresh list
    } catch (error: any) {
      console.error('Error deleting blueprint:', error)
      
      // Handle different error types gracefully
      if (error.response?.status === 401) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to delete blueprints',
          variant: 'destructive'
        })
      } else if (error.response?.status === 403) {
        toast({
          title: 'Access Denied',
          description: 'You do not have permission to delete this blueprint',
          variant: 'destructive'
        })
      } else {
        toast({
          title: 'Error',
          description: error.response?.data?.message || error.message || 'Failed to delete blueprint',
          variant: 'destructive'
        })      }
    }
  }
  const handleAddToProject = (blueprint: BlueprintWithDetails) => {
    try {
      // Store the blueprint in the store so the Flow component can pick it up
      setPendingBlueprint(blueprint)
      
      toast({
        title: 'Success',
        description: `Blueprint "${blueprint.name}" is ready to be added to your project. Switch to the Flow view to see it.`
      })
      
      console.log('Blueprint ready for addition:', blueprint.name)
    } catch (error: any) {
      console.error('Error preparing blueprint for project:', error)
      
      toast({
        title: 'Error',
        description: error.message || 'Failed to prepare blueprint for project',
        variant: 'destructive'
      })
    }
  }

  const filteredBlueprints = blueprints.filter(blueprint => {
    if (activeTab === 'my-blueprints') {
      // Show user's blueprints - would need user context
      return true // For now show all
    }
    if (activeTab === 'public') {
      return blueprint.isPublic
    }
    return true
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Blueprints</h1>
          <p className="text-muted-foreground">
            Create and manage AI-powered workflow automation blueprints
          </p>
        </div>
        <Button onClick={handleCreateBlueprint}>
          <Plus className="h-4 w-4 mr-2" />
          Create Blueprint
        </Button>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search blueprints..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {BLUEPRINT_CATEGORIES.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="my-blueprints">My Blueprints</TabsTrigger>
          <TabsTrigger value="public">Public Blueprints</TabsTrigger>
        </TabsList>

        <TabsContent value="my-blueprints" className="space-y-4">
          {loading ? (
            <div className="text-center py-8">Loading blueprints...</div>
          ) : filteredBlueprints.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No blueprints found</p>
              <Button onClick={handleCreateBlueprint} className="mt-4">
                Create Your First Blueprint
              </Button>
            </div>
          ) : (
            <div className={
              viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }>              {filteredBlueprints.map((blueprint) => (
                <BlueprintCard
                  key={blueprint.id}
                  blueprint={blueprint}
                  onEdit={handleEditBlueprint}
                  onRun={handleRunBlueprint}
                  onDuplicate={handleDuplicateBlueprint}
                  onDelete={handleDeleteBlueprint}
                  onAddToProject={handleAddToProject}
                  onSimulate={handleSimulateBlueprint}
                  onViewDetails={handleViewBlueprintDetails}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="public" className="space-y-4">
          {loading ? (
            <div className="text-center py-8">Loading public blueprints...</div>
          ) : filteredBlueprints.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No public blueprints found</p>
            </div>
          ) : (
            <div className={
              viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                : 'space-y-4'
            }>              {filteredBlueprints.map((blueprint) => (
                <BlueprintCard
                  key={blueprint.id}
                  blueprint={blueprint}
                  onRun={handleRunBlueprint}
                  onDuplicate={handleDuplicateBlueprint}
                  onDelete={handleDeleteBlueprint}
                  onAddToProject={handleAddToProject}
                  onSimulate={handleSimulateBlueprint}
                  onViewDetails={handleViewBlueprintDetails}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>      {/* Stats */}
      <div className="flex justify-center pt-4">
        <div className="text-sm text-muted-foreground">
          Showing {filteredBlueprints.length} blueprint{filteredBlueprints.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Blueprint Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent 
          className="sm:max-w-[95vw] max-h-[95vh] w-[95vw] h-[95vh] p-0 flex flex-col"
        >
          <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
            <DialogTitle>
              {editorMode === 'create' ? 'Create Blueprint' : editorMode === 'edit' ? 'Edit Blueprint' : 'View Blueprint'}
            </DialogTitle>
            <DialogDescription>
              {editorMode === 'create' 
                ? 'Create a new blueprint to define your workflow automation'
                : editorMode === 'edit'
                ? 'Modify your existing blueprint configuration'
                : 'View blueprint details and configuration'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0">
            <BlueprintEditor
              blueprint={editingBlueprint}
              mode={editorMode}
              onSave={handleBlueprintSave}
              onCancel={handleEditorCancel}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Add delete confirmation dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Blueprint</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{blueprintToDelete?.name}"? This action cannot be undone.
              The blueprint will be marked as inactive but its data will be preserved for historical purposes.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteBlueprint}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
